import { useCan } from '../../core/authorization/PermissionProvider';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import BasicCli from '../../main/components/basicCli/BasicCli';
import { ApiConstants } from '../../main/constants/ApiConstants';
import axiosInstance from '../../main/initializers/axios';
import DeleteModal from '../../main/components/modal/delete/DeleteModal';
import EditModal from '../../main/components/modal/edit/EditModal';
import eFormType from '../../main/assets/enums/eFormType';
import IUser from '../../main/interfaces/IUser';
import IRole from '../../main/interfaces/IRole';
import IPagination from '../../main/interfaces/IPagination';
import IGridItems from '../../main/interfaces/IGridItems';
import GeneralTable from '../../main/components/grid/GeneralTable';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined';
import './NotificationAdmin.css';

/*
 * Platform ids are GUID strings. The legacy screen coerced every select value with `Number(...)`,
 * which turned a GUID into `NaN` (serialised as `null`), so every save was refused with
 * "A category id is required". The ids are kept as the strings the selects hold; `0` still
 * means "new row" / "nothing picked", which PlatformAPI reads as absent.
 */
type EntityId = number | string;
type Category = { id: EntityId; name: string; type: number };
type UserCategoryRow = {
  id: EntityId;
  companyId: EntityId;
  categoryId: EntityId;
  category?: string;
  targetType: string;
  type?: string;
  roleId?: EntityId | null;
  role?: string;
  userId?: EntityId | null;
  user?: string;
  isActive: boolean;
  createdAt?: string;
};

const emptyRow: UserCategoryRow = {
  id: 0,
  companyId: 0,
  categoryId: 0,
  /*
   * PlatformAPI stores a rule for ONE user or ONE role (PlatformDB script 026). A role rule
   * reaches every active member holding that role, including members added to it later.
   */
  targetType: 'Role',
  roleId: null,
  userId: null,
  isActive: true,
};

/** True for a persisted row's id; `0` / blank is the legacy "new row" sentinel. */
const hasId = (id: EntityId | null | undefined) =>
  id !== null && id !== undefined && `${id}` !== '' && `${id}` !== '0';

const normalizeUserCategoryForm = (row: UserCategoryRow): UserCategoryRow => {
  const targetType = row.targetType === 'User' ? 'User' : 'Role';

  return {
    ...row,
    targetType,
    roleId: targetType === 'Role' ? row.roleId ?? null : null,
    userId: targetType === 'User' ? row.userId ?? null : null,
  };
};

const defaultUserCategoryGridItems: IGridItems[] = [
  { entity: 'Id', sort: 'desc', filter: null, isShown: true, label: 'Id' },
  { entity: 'Category', sort: null, filter: null, isShown: true, label: 'CategoryId' },
  { entity: 'Type', sort: null, filter: null, isShown: true, label: 'TargetType' },
  { entity: 'Role', sort: null, filter: null, isShown: true, label: 'RoleId' },
  { entity: 'User', sort: null, filter: null, isShown: true, label: 'UserId' },
  { entity: 'IsActive', sort: null, filter: null, isShown: true, label: 'IsActive' },
  { entity: 'CreatedAt', sort: null, filter: null, isShown: true, label: 'CreatedAt' },
];

const normalizeUserCategoryGridItems = (items?: IGridItems[]) => {
  const validEntities = new Set(['id', 'categoryid', 'category', 'targettype', 'type', 'roleid', 'role', 'userid', 'user', 'isactive', 'createdat']);
  const nextItems = (items ?? []).filter((x) => validEntities.has(String(x.entity ?? '').toLowerCase()));
  return nextItems.length ? nextItems : defaultUserCategoryGridItems;
};

const NotificationUserCategoriesGrid = () => {
  const canCreate = useCan("platform.notification-rules", "create");
  const canUpdate = useCan("platform.notification-rules", "update");
  const canDelete = useCan("platform.notification-rules", "delete");
  const { i18n, t } = useTranslation();
  const [rows, setRows] = useState<UserCategoryRow[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [roles, setRoles] = useState<IRole[]>([]);
  const [users, setUsers] = useState<IUser[]>([]);
  const [gridItems, setGridItems] = useState<IGridItems[]>([]);
  const [pagination, setPagination] = useState<IPagination>({ count: 1, page: 1, itemPerPage: 50, itemsPerPage: [10, 20, 50, 100] });
  const [selected, setSelected] = useState<UserCategoryRow | null>(null);
  const [form, setForm] = useState<UserCategoryRow>(emptyRow);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);

  const targetTypeLabel = useCallback(
    (targetType?: string) =>
      t(`notifications.targetTypes.${targetType}`, {
        defaultValue: targetType || '',
      }),
    [t],
  );

  const translatedRows = useMemo(
    () =>
      rows.map((row) => ({
        ...row,
        type: targetTypeLabel(row.targetType || row.type),
      })),
    [rows, targetTypeLabel],
  );

  const load = useCallback(
    async (pag?: IPagination, gridOverride?: IGridItems[]) => {
      const effective = pag ?? pagination;
      const effectiveGrid = gridOverride ?? gridItems ?? [];
      const [rulesRes, categoriesRes, rolesRes, usersRes] = await Promise.all([
        axiosInstance.post(ApiConstants.notifications.userCategories.listCt, {
          gridItems: effectiveGrid,
          pagination: effective,
        }),
        axiosInstance.get(ApiConstants.notifications.categories.list),
        axiosInstance.get(ApiConstants.Roles.list),
        axiosInstance.get(ApiConstants.User.users),
      ]);

      setRows(rulesRes.data?.items ?? []);
      setGridItems(normalizeUserCategoryGridItems(rulesRes.data?.gridItems ?? []));
      setPagination((prev) => ({
        ...prev,
        count: rulesRes.data?.pagination?.count ?? prev.count,
        page: rulesRes.data?.pagination?.page ?? prev.page,
        itemPerPage: rulesRes.data?.pagination?.itemPerPage ?? prev.itemPerPage,
        itemsPerPage: rulesRes.data?.pagination?.itemsPerPage ?? prev.itemsPerPage,
      }));
      setCategories(categoriesRes.data ?? []);
      setRoles(rolesRes.data ?? []);
      setUsers(usersRes.data ?? []);
    },
    [gridItems, pagination],
  );

  useEffect(() => {
    void load();
  }, []);

  const openNew = () => {
    if (!canCreate) return;
    setForm(normalizeUserCategoryForm(emptyRow));
    setSelected(null);
    setIsEditOpen(true);
  };

  const openEdit = (row: UserCategoryRow) => {
    setSelected(row);
    setForm(normalizeUserCategoryForm(row));
    setIsEditOpen(true);
  };

  const save = async () => {
    if (selected ? !canUpdate : !canCreate) return;
    await axiosInstance.post(ApiConstants.notifications.userCategories.save, normalizeUserCategoryForm(form));
    setIsEditOpen(false);
    await load();
  };

  const remove = async () => {
    if (!selected || !canDelete) return;
    await axiosInstance.post(ApiConstants.notifications.userCategories.delete(selected.id));
    setIsDeleteOpen(false);
    setSelected(null);
    await load();
  };

  return (
    <BasicCli>
      <div className="naWrap bcPageContent">
        <div className="naContainer">
          <div className="bcPageHeader naPageHeader">
            <div className="naHeading">
              <span className="naHeadingIcon"><GroupOutlinedIcon /></span>
              <div>
              <h1 className="bcPageTitle">{i18n.t('notifications.userCategoryTitle', { defaultValue: 'Notification User Groups' })}</h1>
              <p className="naSubtitle">{i18n.t('notifications.userCategorySubtitle', { defaultValue: 'Configure who receives each notification category.' })}</p>
              </div>
            </div>
            <div className="bcPageHeaderActions">
              <button className="ui-control ui-btn" type="button" onClick={() => void load()}><RefreshRoundedIcon fontSize="small" />{i18n.t('notifications.refresh', { defaultValue: 'Refresh' })}</button>
              <button className="ui-control ui-btn ui-btn--primary" type="button" onClick={openNew} disabled={!canCreate}><AddRoundedIcon fontSize="small" />{i18n.t('generic.createNew', { defaultValue: 'Create new' })}</button>
            </div>
          </div>

          <div className="naTableWrap">
            <GeneralTable
              data={translatedRows}
              columns={normalizeUserCategoryGridItems(gridItems)}
              gridEntity={20}
              title=""
              hasRp={true}
              selectedItem={(val) => setSelected(val)}
              onEdit={openEdit}
              canEdit={() => canUpdate}
              canDelete={() => canDelete}
              onDelete={(row) => {
                setSelected(row);
                setIsDeleteOpen(true);
              }}
              pagination={pagination}
              selectedItemPagination={(val) => void load(val)}
              onFilterClose={(nextGridItems) => {
                const normalized = normalizeUserCategoryGridItems(nextGridItems);
                setGridItems(normalized);
                void load(undefined, normalized);
              }}
            />
          </div>
        </div>
      </div>

      {isEditOpen && (
        <EditModal
          title={hasId(form.id)
            ? i18n.t('notifications.editUserCategory', { defaultValue: 'Edit User Group Rule' })
            : i18n.t('notifications.newUserCategory', { defaultValue: 'New User Group Rule' })}
          width="760px"
          closeModal={() => setIsEditOpen(false)}
          setIsModalOpen={setIsEditOpen}
          onSubmitData={() => void save()}
          modalType={hasId(form.id) ? eFormType.Edit : eFormType.New}
        >
          <div className="naForm">
            <label className="naField">
              <span>{i18n.t('notifications.category', { defaultValue: 'Category' })}</span>
              <select className="ui-control ui-select" value={form.categoryId} onChange={(e) => setForm((prev) => ({ ...prev, categoryId: e.target.value }))}>
                <option value={0}>{i18n.t('notifications.selectCategory', { defaultValue: 'Select category' })}</option>
                {categories.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
              </select>
            </label>
            <label className="naField">
              <span>{i18n.t('notifications.targetType', { defaultValue: 'Target Type' })}</span>
              <select className="ui-control ui-select" value={form.targetType} onChange={(e) => setForm((prev) => normalizeUserCategoryForm({ ...prev, targetType: e.target.value }))}>
                <option value="Role">{targetTypeLabel('Role')}</option>
                <option value="User">{targetTypeLabel('User')}</option>
              </select>
            </label>
            <label className="naField">
              <span>{i18n.t('generic.role', { defaultValue: 'Role' })}</span>
              <select
                className="ui-control ui-select"
                value={form.targetType === 'Role' ? form.roleId ?? '' : ''}
                disabled={form.targetType !== 'Role'}
                onChange={(e) => setForm((prev) => ({ ...prev, roleId: e.target.value || null }))}
              >
                <option value="">{i18n.t('generic.none', { defaultValue: 'None' })}</option>
                {roles.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
              </select>
            </label>
            <label className="naField">
              <span>{i18n.t('menu.Users', { defaultValue: 'Users' })}</span>
              <select
                className="ui-control ui-select"
                value={form.targetType === 'User' ? form.userId ?? '' : ''}
                disabled={form.targetType !== 'User'}
                onChange={(e) => setForm((prev) => ({ ...prev, userId: e.target.value || null }))}
              >
                <option value="">{i18n.t('generic.none', { defaultValue: 'None' })}</option>
                {users.map((item) => <option key={item.id} value={item.id}>{item.firstName} {item.lastName}</option>)}
              </select>
            </label>
            <div className="naToggleGrid"><label className="naCheck"><input className="ui-check" type="checkbox" checked={form.isActive} onChange={(e) => setForm((prev) => ({ ...prev, isActive: e.target.checked }))} /> <span><strong>{i18n.t('notifications.active', { defaultValue: 'Active' })}</strong><small>Deliver notifications to this target</small></span></label></div>
          </div>
        </EditModal>
      )}

      {isDeleteOpen && selected && (
        <DeleteModal width="460px" handleClose={() => setIsDeleteOpen(false)} handleDelete={() => void remove()}>
          {i18n.t('notifications.deleteUserCategoryConfirm', {
            defaultValue: 'Delete notification user group rule {{id}}?',
            id: selected.id,
          })}
        </DeleteModal>
      )}
    </BasicCli>
  );
};

export default NotificationUserCategoriesGrid;
